import React, { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "~/components/dialog";
import DropdownFormField from "~/visuals/organisms/DropdownFormField";
import { useSnackBar } from "~/components/snackbar";
import { Subtitle1 } from "@material/react-typography";
import FormBottomRow from "~/components/form-bottom-row";
import Button from "~/components/button";
import Spinner from "~/components/spinner";
import { useRequestProjectFolderMoveMutation } from "../mutations.hotchoc.generated";
import { MutationForm, ErrorRenderer } from "~/components/forms/MutationForm";
import { ProjectFolder, RequestMoveFormValues } from "../types";
import { ValidationResponse } from "~/gql/types";
import { SharedDrive } from "~/gql/types2";
import { useProjectFoldersPageApi } from "../ProjectFoldersPageApi";
import { mapDriveOptions } from "../utils";

type RequestProjectFolderMoveProps = {
  folder: ProjectFolder;
  driveOptions: SharedDrive[];
};

export const RequestProjectFolderMove: React.FC<
  RequestProjectFolderMoveProps
> = ({ folder, driveOptions }) => {
  const addAlert = useSnackBar();
  const { reload } = useProjectFoldersPageApi();
  const [openRequestMove, setOpenRequestMove] = useState<boolean>(false);

  const [requestProjectFolderMove, { loading }] =
    useRequestProjectFolderMoveMutation();

  const options = mapDriveOptions(driveOptions, folder.fullPath);

  const runMutation = async (values: RequestMoveFormValues) => {
    const variables = {
      input: {
        folderId: folder.id,
        driveLetter: values.driveLetter!,
      },
    };

    const result = await requestProjectFolderMove({ variables });
    return result.data?.projectFolders
      ?.requestProjectFolderMove as ValidationResponse;
  };

  const handleSuccess = () => {
    setOpenRequestMove(false);
    addAlert({
      key: `move-to-drive${folder.name}`,
      message: `Successfully requested move for ${folder.name}`,
      isSuccess: true,
    });
    reload();
  };

  return (
    <>
      <Button onClick={() => setOpenRequestMove(true)}>
        Request Folder Move
      </Button>
      <Dialog open={openRequestMove} onClose={() => setOpenRequestMove(false)}>
        <>
          <Spinner open={loading} />
          <DialogTitle>Request Folder Move</DialogTitle>
          <DialogContent>
            <Subtitle1>{`This will send a request to the helpdesk to move ${folder.name} to the selected drive.`}</Subtitle1>
            <MutationForm
              onSuccess={handleSuccess}
              runMutation={runMutation}
              initialValues={{ driveLetter: null }}
              allowPristineSubmit={false}
            >
              <DropdownFormField
                label="Drive letter"
                formField="driveLetter"
                helperText=""
                options={options}
              />
              <ErrorRenderer
                render={(submitError) => (
                  <FormBottomRow
                    buttonText="Save"
                    errorMessage={submitError}
                    onCancel={() => setOpenRequestMove(false)}
                  />
                )}
              />
            </MutationForm>
          </DialogContent>
        </>
      </Dialog>
    </>
  );
};
